import { useContext, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { DatePicker } from "../ui/datepicker";
import { X } from "lucide-react";
import { cn, handleApiError } from "../../lib/utils";
import apiRequest, { ApiResponse } from "../../axios/useApi";
import { AllowableFields } from "../../pages/Payments/PaymentFilter";
import { motion } from "framer-motion";
import { UIContext } from "../../providers/UIProvider";
import CustomReactSelect from "../ui/custom-react-select";
import { Customer } from "../../providers/CustomerProvider";

export interface Filters {
  startDate?: string; // ISO string for the start date
  endDate?: string; // ISO string for the end date
  customerId?: string; // Customer ID as a string
  status?: string; // Optional status field if needed
}

interface FilterType<TData, TValue> {
  allowableFields?: AllowableFields[];
  statusList?: { label: string; value: string }[];
  onApplyFilter?: (data: Filters) => void;
  onClearFilter?: () => void;
  clearFilterText?: string;
  btnText?: string;
  label?: string;
  FilterFieldsLabel?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    customer?: string;
  };
}

export function Filter<TData, TValue>({
  label,
  allowableFields = ["status", "startDate", "endDate", "customer"],
  statusList = [],
  FilterFieldsLabel = {
    status: "Status",
    startDate: "Start Date",
    endDate: "End Date",
    customer: "Customer",
  },
  onApplyFilter,
  onClearFilter,
  clearFilterText = "Clear all Filter",
  btnText = "Apply Filter",
}: FilterType<TData, TValue>) {
  // Add these new states
  const { onError, businessId } = useContext(UIContext);
  const [customerOptions, setCustomerOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [filters, setFilters] = useState({
    status: "",
    startDate: undefined,
    endDate: undefined,
    customerId: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    status: "",
    startDate: undefined,
    endDate: undefined,
    customerId: "",
  });

  // Handle filter changes in one function
  const handleFilterChange = (field: string, value: any) => {
    if (field === "status" || field === "customerId") {
      setFilters((prev) => ({ ...prev, [field]: value?.value || "" }));
    } else {
      setFilters((prev) => ({ ...prev, [field]: value || "" }));
    }
  };

  // Handle the "Clear Filter" logic
  const clearFilter = () => {
    setFilters({
      status: "",
      startDate: undefined,
      endDate: undefined,
      customerId: "",
    });
    setAppliedFilters({
      status: "",
      startDate: undefined,
      endDate: undefined,
      customerId: "",
    });
    if (onClearFilter) {
      onClearFilter && onClearFilter();
    }
  };

  const clearSingleFilter = (field: string) => {
    const updatedFilters = { ...filters };
    const updatedAppliedFilters = { ...appliedFilters };

    if (field === "status") {
      updatedFilters.status = "";
      updatedAppliedFilters.status = "";
    } else if (field === "startDate") {
      updatedFilters.startDate = undefined;
      updatedAppliedFilters.startDate = undefined;
    } else if (field === "endDate") {
      updatedFilters.endDate = undefined;
      updatedAppliedFilters.endDate = undefined;
    } else if (field === "customerId") {
      updatedFilters.customerId = "";
      updatedAppliedFilters.customerId = "";
    }

    setFilters(updatedFilters);
    setAppliedFilters(updatedFilters);

    // Apply the updated filters
    if (onApplyFilter) {
      onApplyFilter(updatedFilters);
    }
  };

  const isFieldAllowed = (
    field: "status" | "startDate" | "endDate" | "customer"
  ) => {
    return allowableFields.includes(field);
  };

  const handleApplyFilter = () => {
    const newAppliedFilters = {
      status: filters.status || "",
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      customerId: filters.customerId || "",
    };
    setAppliedFilters(newAppliedFilters);
    if (onApplyFilter) {
      onApplyFilter(newAppliedFilters);
    }
  };

  const formatDate = (date: Date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-GB");
  };

  const hasAppliedFilters = () => {
    return (
      appliedFilters.status ||
      appliedFilters.startDate ||
      appliedFilters.endDate ||
      appliedFilters.customerId
    );
  };

  // Get display label for status
  const getStatusLabel = (value: string) => {
    const statusItem = statusList.find((item) => item.value === value);
    return statusItem ? statusItem.label : value;
  };

  const fetchCustomers = async (searchValue: string) => {
    // setIsLoadingCustomers(true);
    try {
      const queryParams = new URLSearchParams({
        filterText: searchValue,
      });

      const response: ApiResponse<Customer[]> = await apiRequest({
        url: `/customer/${businessId}/customerAutoComplete?${queryParams}`,
        method: "GET",
      });
      if (response.success) {
        const options = response?.data?.map((customer: Customer) => ({
          label:
            customer.externalId +
            " - " +
            customer.firstName +
            " " +
            customer.lastName,
          value: customer.customerId,
        }));
        setCustomerOptions(options || []);
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {}
    // setIsLoadingCustomers(false);
  };

  // Fetch customers initially when the component mounts
  useEffect(() => {
    if (allowableFields.includes("customer")) {
      fetchCustomers(""); // Fetch default customer data
    }
  }, []); // Fetch only if customer field is allowed

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} // Start with 0 opacity
        animate={{ opacity: 1 }} // Animate to 1 opacity
        transition={{ duration: 0.7 }} // 1 second fade-in duration
      >
        <div className="flex flex-wrap justify-between items-center gap-4 w-full mb-2">
          <div>
            {label && (
              <h3 className="BookEZ_font_heading BookEZ_font_bold BookEZ_font_header_color mb-1.5 mr-4">
                {label}
              </h3>
            )}
          </div>

          {/* Responsive Filter Fields Container */}
          <div className="flex flex-wrap gap-2">
            {isFieldAllowed("status") && (
              <div className="w-auto">
                <CustomReactSelect
                  id="status"
                  name="status"
                  options={statusList}
                  value={
                    statusList?.find((item) => item.value === filters.status) ||
                    null
                  }
                  onChange={(value: any) => handleFilterChange("status", value)}
                  placeholder={FilterFieldsLabel?.status}
                  isClearable
                />
              </div>
            )}

            {isFieldAllowed("startDate") && (
              <div className="w-auto">
                <DatePicker
                  label={FilterFieldsLabel?.startDate}
                  date={filters.startDate}
                  maxDate={filters.endDate || undefined}
                  onDateChange={(date) => handleFilterChange("startDate", date)}
                />
              </div>
            )}

            {isFieldAllowed("endDate") && (
              <div className="w-auto">
                <DatePicker
                  label={FilterFieldsLabel?.endDate}
                  date={filters.endDate}
                  minDate={filters.startDate || undefined} // Disable dates before the selected start date
                  onDateChange={(date) => handleFilterChange("endDate", date)}
                />
              </div>
            )}

            {isFieldAllowed("customer") && (
              <div className="w-auto">
                <CustomReactSelect
                  id="customer"
                  name="customer"
                  options={customerOptions}
                  value={
                    customerOptions?.find(
                      (option) => option.value === filters.customerId
                    ) || ""
                  }
                  onChange={(value: any) => {
                    handleFilterChange("customerId", value);
                  }}
                  placeholder={FilterFieldsLabel.customer}
                  isClearable
                />
              </div>
            )}

            {/* Apply Button - Responsive positioning */}
            {/* <div className="w-full sm:col-span-2 lg:col-span-1 xl:col-span-1 flex justify-start sm:justify-start"> */}
            <Button
              onClick={handleApplyFilter}
              variant={"outline"}
              className={cn(
                "border w-full sm:w-auto min-w-[120px] max-w-[300px]"
              )}
            >
              {btnText}
            </Button>
            {/* </div> */}
          </div>
        </div>

        {/* Applied Filters Display - Responsive */}
        {hasAppliedFilters() && (
          <div className="flex flex-wrap gap-1 sm:gap-2 items-center justify-start sm:justify-end my-2 sm:mt-3">
            {appliedFilters.status && (
              <div className="flex BookEZ_font_color_gray_2 items-center BookEZ_card_bg BookEZ_card_border_color px-2 sm:px-3 py-1 border rounded-2xl text-xs sm:text-sm">
                <span className="mr-1 BookEZ_font_body_medium BookEZ_filter_text">
                  {FilterFieldsLabel?.status?.toLowerCase().includes("status")
                    ? "Status | "
                    : FilterFieldsLabel?.status?.replace(/^Select\s+/i, "") +
                      " | "}
                </span>

                <span className="mx-1 BookEZ_font_body_medium BookEZ_filter_applied ">
                  {getStatusLabel(appliedFilters.status)}
                </span>
                <button
                  onClick={() => clearSingleFilter("status")}
                  className="ml-1 text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <X
                    size={12}
                    className="sm:w-3.5 sm:h-3.5 BookEZ_error_text"
                  />
                </button>
              </div>
            )}

            {appliedFilters.customerId && (
              <div className="flex items-center BookEZ_font_color_gray_2 BookEZ_card_bg BookEZ_card_border_color px-2 sm:px-3 py-1 border rounded-2xl text-xs sm:text-sm">
                <span className="mr-1  BookEZ_font_body_medium BookEZ_filter_text">
                  {"Customer"}
                </span>{" "}
                |{" "}
                <span className="mx-1 BookEZ_font_body_medium BookEZ_filter_applied max-w-[100px] sm:max-w-[150px] truncate">
                  {customerOptions?.find(
                    (option) => option.value === appliedFilters.customerId
                  )?.label || ""}
                </span>
                <button
                  onClick={() => clearSingleFilter("customerId")}
                  className="ml-1 text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <X
                    size={12}
                    className="sm:w-3.5 sm:h-3.5 BookEZ_error_text"
                  />
                </button>
              </div>
            )}

            {appliedFilters.startDate && (
              <div className="flex items-center BookEZ_font_color_gray_2 BookEZ_card_bg BookEZ_card_border_color px-2 sm:px-3 py-1 border rounded-2xl text-xs sm:text-sm">
                <span className="mr-1 BookEZ_font_body_medium BookEZ_filter_text">
                  {FilterFieldsLabel.startDate}
                </span>{" "}
                |{" "}
                <span className="mx-1 BookEZ_font_body_medium BookEZ_filter_applied">
                  {formatDate(appliedFilters.startDate)}
                </span>
                <button
                  onClick={() => clearSingleFilter("startDate")}
                  className="ml-1 text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <X
                    size={12}
                    className="sm:w-3.5 sm:h-3.5 BookEZ_error_text"
                  />
                </button>
              </div>
            )}

            {appliedFilters.endDate && (
              <div className="flex BookEZ_font_color_gray_2 items-center BookEZ_card_bg BookEZ_card_border_color px-2 sm:px-3 py-1 border rounded-2xl text-xs sm:text-sm">
                <span className="mr-1 BookEZ_font_body_medium BookEZ_filter_text">
                  {FilterFieldsLabel.endDate}
                </span>{" "}
                |{" "}
                <span className="mx-1 BookEZ_font_body_medium BookEZ_filter_applied">
                  {formatDate(appliedFilters.endDate)}
                </span>
                <button
                  onClick={() => clearSingleFilter("endDate")}
                  className="ml-1 text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <X
                    size={12}
                    className="sm:w-3.5 sm:h-3.5 BookEZ_error_text"
                  />
                </button>
              </div>
            )}

            <button
              onClick={clearFilter}
              className={cn(
                "px-2 sm:px-3 py-1 rounded-2xl border transition-colors duration-200 BookEZ_error_border BookEZ_error_transparent hover:bg-red-200 BookEZ_error_text text-xs sm:text-sm"
              )}
            >
              <span className="hidden sm:inline">{clearFilterText}</span>
              <span className="sm:hidden">Clear all Filter</span>
            </button>
          </div>
        )}
      </motion.div>
    </>
  );
}
